import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import Button from '../ui/Button';
import { ErrorNotice, InfoNotice } from '../ui/States';
import { DOCUMENTS, EventDetail, dayLabel } from '../../lib/events';
import { errorMessage } from '../../lib/programs';
import { openBlankTab, showPrintable } from '../../lib/printable';
import { useAgenda } from './agendaTypes';
import { NametagCsvLink } from './NametagCsvButton';

interface Output {
  kind: string;
  title: string;
  note: string;
  langs: boolean;
  audience: boolean;
  csv?: boolean;
  adminOnly?: boolean;
}

// In the order the tab lists them.
const OUTPUTS: Output[] = [
  {
    kind: 'agenda',
    title: 'Agenda',
    note: 'Printable agenda for the chosen day or audience (for example core members or associate fellows)',
    langs: true,
    audience: true,
  },
  {
    kind: 'website',
    title: 'Website event page text',
    note: 'Program and speaker bios to paste into the WordPress event page',
    langs: true,
    audience: true,
  },
  {
    kind: 'package',
    title: 'Speaker packages',
    note: 'One page per speaker: sessions, what we have, what’s missing, slides and arrival. Prepared, never sent',
    langs: true,
    audience: false,
    adminOnly: true,
  },
  {
    kind: 'av',
    title: 'AV, speaker, emcee and title list',
    note: 'For the AV team and emcees: time, role, name, title, room, format and slide file name',
    langs: false,
    audience: true,
    adminOnly: true,
  },
  {
    kind: 'interpreter-brief',
    title: 'Interpreter brief',
    note: 'Interpreted sessions with room, Zoom link, recording, speakers and titles, and the slide and glossary links',
    langs: false,
    audience: true,
    adminOnly: true,
  },
  {
    kind: 'av-brief',
    title: 'AV brief',
    note: 'Every session with room, online, Zoom link, interpretation, recording and people, plus the greetings playlist',
    langs: false,
    audience: true,
    adminOnly: true,
  },
  {
    kind: 'rollout',
    title: 'Emcee roll-out',
    note: 'Who introduces whom in each session, with the bio to read. Add the logistics yourself',
    langs: false,
    audience: true,
    adminOnly: true,
  },
  {
    kind: 'pheedloop-sessions',
    title: 'PheedLoop sessions file',
    note: 'CSV: Title, Date, Start Time, End Time, Speakers, Location / Room',
    langs: false,
    audience: true,
    csv: true,
    adminOnly: true,
  },
  {
    kind: 'pheedloop-speakers',
    title: 'PheedLoop speakers file',
    note: 'CSV of every speaker: First Name, Last Name, Email Address, Job Title, Company, Number of Sessions, Bio, Speaker Picture (pictures are links)',
    langs: false,
    audience: false,
    csv: true,
    adminOnly: true,
  },
];

function download(fileName: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Rows stack below 768px: header hidden, each cell on its own line after its column name.
const TABLE = 'w-full border-collapse tabular max-md:block';
const HEAD = 'text-left text-small text-ink-2 border-b border-line-strong max-md:hidden';
const TH = 'py-2 pr-3 last:pr-0 align-bottom font-semibold';
const ROW = 'border-b border-line hover:bg-selected max-md:block max-md:py-3';
const TD = 'py-[11px] pr-3 last:pr-0 align-top text-[15px] leading-[22px] text-ink max-md:block max-md:p-0';
const LABELLED =
  "max-md:before:content-[attr(data-label)_'_·_'] max-md:before:text-ink-3 max-md:before:text-small max-md:before:font-normal";
const ACTS = `${TD} md:text-right whitespace-nowrap max-md:pt-2`;

const made = (at: string) =>
  new Date(at).toLocaleString('en-US', {
    timeZone: 'America/Toronto',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** Lists for the hotel, the caterer and the door: made here (admins only, they carry dietary and accessibility
 *  needs), opened in a new tab to print or save as PDF. */
export function PrintablesSection({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const event = useQuery<EventDetail>(['event', eventId], async () => (await api.get(`/events/${eventId}`)).data.data);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const documents = event.data?.event.documents ?? {};
  const open = async (kind: string) => {
    setError(null);
    setBusy(kind);
    const tab = openBlankTab();
    try {
      const res = await api.post(`/events/${eventId}/documents/${kind}`);
      showPrintable(tab, res.data.data.html);
      ['event', 'events', 'event-content', 'event-dashboard', 'event-followups', 'checklist'].forEach((k) =>
        queryClient.invalidateQueries(k),
      );
    } catch (err) {
      tab?.close();
      setError(errorMessage(err, 'Could not make the document.'));
    } finally {
      setBusy(null);
    }
  };
  return (
    <Panel className="mt-14 max-md:mt-10">
      <PanelHeader title="For the hotel, the caterer and the door" />
      <PanelBody>
        {!canEdit ? (
          <div className="mb-3">
            <InfoNotice>These carry dietary and accessibility needs, so only admins make them.</InfoNotice>
          </div>
        ) : null}
        {error ? (
          <div className="mb-3">
            <ErrorNotice>{error}</ErrorNotice>
          </div>
        ) : null}
        <table className={TABLE}>
          <thead className={HEAD}>
            <tr>
              <th className={TH}>Document</th>
              <th className={TH}>What’s in it</th>
              <th className={TH}>Last made</th>
              {canEdit ? (
                <th className={TH}>
                  <span className="sr-only">Action</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody className="max-md:block">
            {DOCUMENTS.map(([kind, title, note]) => {
              const last = documents[kind];
              return (
                <tr key={kind} className={ROW}>
                  <td className={`${TD} font-semibold`}>{title}</td>
                  <td data-label="Contents" className={`${TD} ${LABELLED} text-ink-2`}>
                    {note}
                  </td>
                  <td data-label="Last made" className={`${TD} ${LABELLED} ${last ? '' : 'text-ink-3'}`}>
                    {last ? `${made(last.at)} · ${last.count} ${last.count === 1 ? 'person' : 'people'}` : '–'}
                  </td>
                  {canEdit ? (
                    <td className={ACTS}>
                      <span className="inline-flex flex-wrap gap-2 items-center md:justify-end">
                        <Button size="sm" disabled={busy !== null} onClick={() => open(kind)}>
                          {busy === kind ? 'Making…' : 'Make it'}
                        </Button>
                        {kind === 'nametags' ? <NametagCsvLink eventId={eventId} canEdit={canEdit} /> : null}
                      </span>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="text-small text-ink-3 mt-3 max-w-[72ch]">
          They list registered and confirmed invitees attending in person. Making the catering numbers and the nametags
          ticks their Timeline steps. Only admins make these, because they carry dietary and accessibility needs.
        </p>
      </PanelBody>
    </Panel>
  );
}

/** Agenda documents: printable pages and PheedLoop files from the agenda and speakers. */
export function AgendaDocuments({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const agenda = useAgenda(eventId);
  const [audience, setAudience] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const ev = agenda.data?.event;

  const open = async (o: Output, lang: 'en' | 'fr') => {
    setError(null);
    setBusy(`${o.kind}-${lang}`);
    const tab = o.csv ? null : openBlankTab();
    try {
      const [kind, value] = audience.split(':');
      const params = new URLSearchParams({ lang });
      if (o.audience && kind === 'day') params.set('day', value);
      if (o.audience && kind === 'audience') params.set('audience', value);
      const res = await api.get(`/events/${eventId}/agenda/documents/${o.kind}?${params}`);
      if (o.csv) download(res.data.data.fileName, res.data.data.csv);
      else showPrintable(tab, res.data.data.html);
    } catch (err) {
      tab?.close();
      setError(errorMessage(err, 'Could not make the document'));
    } finally {
      setBusy(null);
    }
  };

  const id = `agenda-docs-for-${eventId}`;
  return (
    <Panel className="mt-14 max-md:mt-10">
      <PanelHeader title="Agenda documents">
        <label htmlFor={id} className="text-[15px] leading-5 font-semibold text-ink">
          For
        </label>
        <select
          id={id}
          className="min-h-[40px] px-3 py-2 border border-line-strong rounded-sm bg-surface text-[15px] leading-[22px] text-ink max-md:flex-1 max-md:min-w-0"
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
        >
          <option value="">The whole event</option>
          {ev?.days.map((d) => (
            <option key={d.date} value={`day:${d.date}`}>
              {dayLabel(d.date)}
              {d.label ? ` · ${d.label}` : ''}
            </option>
          ))}
          {ev?.audiences.map((a) => (
            <option key={a.key} value={`audience:${a.key}`}>
              {a.name}
            </option>
          ))}
        </select>
      </PanelHeader>
      <PanelBody>
        {error ? (
          <div className="mb-3">
            <ErrorNotice>{error}</ErrorNotice>
          </div>
        ) : null}
        <table className={TABLE}>
          <thead className={HEAD}>
            <tr>
              <th className={TH}>Document</th>
              <th className={TH}>What’s in it</th>
              <th className={TH}>
                <span className="sr-only">Open</span>
              </th>
            </tr>
          </thead>
          <tbody className="max-md:block">
            {OUTPUTS.filter((o) => !o.adminOnly || canEdit).map((o) => (
              <tr key={o.kind} className={ROW}>
                <td className={`${TD} font-semibold`}>{o.title}</td>
                <td data-label="Contents" className={`${TD} ${LABELLED} text-ink-2`}>
                  {o.note}
                  {o.audience ? '' : '. Always the whole event'}
                </td>
                <td className={ACTS}>
                  <span className="inline-flex gap-2 md:justify-end">
                    <Button size="sm" disabled={busy !== null} onClick={() => open(o, 'en')}>
                      {o.csv ? 'Download' : o.langs ? 'English' : 'Open'}
                    </Button>
                    {o.langs ? (
                      <Button size="sm" disabled={busy !== null} onClick={() => open(o, 'fr')}>
                        Français
                      </Button>
                    ) : null}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-small text-ink-3 mt-3 max-w-[72ch]">
          Where a French title, description or bio is missing, the English one is used.
        </p>
      </PanelBody>
    </Panel>
  );
}

/** The Documents tab's middle sections, in the order the tab reads: the lists for the hotel, the caterer and the
 *  door, then the agenda documents (plugins.ts lists this panel after the information packages). */
export default function AgendaOutputs({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  return (
    <>
      <PrintablesSection eventId={eventId} canEdit={canEdit} />
      <AgendaDocuments eventId={eventId} canEdit={canEdit} />
    </>
  );
}
